import type { RouteObject } from 'react-router-dom';
import type {
  ModuleDashboardWidget,
  ModuleFrontendDefinition,
  ModuleNavContribution,
} from './types';

/**
 * Typed module registry, built at BUILD TIME from a Vite glob — the same
 * mechanism i18n namespaces use. Each `src/modules/<id>/index.ts` default-
 * exports a ModuleFrontendDefinition. No runtime fetching of code; a module
 * that is not in the repo simply is not in the bundle.
 *
 * Runtime enabled-state is resolved separately (useModules / the module's
 * permission), so this registry is purely the static contribution surface.
 */
const moduleGlobs = import.meta.glob('./*/index.tsx', { eager: true }) as Record<
  string,
  { default?: ModuleFrontendDefinition }
>;

function loadDefinitions(): ModuleFrontendDefinition[] {
  const defs: ModuleFrontendDefinition[] = [];

  for (const path in moduleGlobs) {
    const def = moduleGlobs[path]?.default;
    if (def && typeof def.id === 'string') {
      defs.push(def);
    }
  }

  // Stable order by id for deterministic route/nav assembly.
  return defs.sort((a, b) => a.id.localeCompare(b.id));
}

export const moduleDefinitions: ModuleFrontendDefinition[] = loadDefinitions();

export function getModuleById(id: string): ModuleFrontendDefinition | undefined {
  return moduleDefinitions.find((m) => m.id === id);
}

/**
 * All module routes, flattened. Each is mounted regardless of enabled state;
 * the route element is wrapped (by the module) in ProtectedRoute (permission)
 * + ModuleEnabledGate (runtime flag), so a disabled module's page is never
 * actually reachable — the permission is absent and the gate 404s.
 */
export function getModuleRoutes(): RouteObject[] {
  return moduleDefinitions.flatMap((m) => m.routes ?? []);
}

/**
 * All module nav contributions, flattened. The core Sidebar merges these into
 * its item list; its existing permission filter hides any whose gating
 * permission the user lacks (i.e. disabled modules).
 */
export function getModuleNavContributions(): ModuleNavContribution[] {
  return moduleDefinitions.flatMap((m) => m.nav ?? []);
}

/**
 * All module dashboard-widget contributions, flattened.
 */
export function getModuleDashboardWidgets(): ModuleDashboardWidget[] {
  return moduleDefinitions.flatMap((m) => m.dashboardWidgets ?? []);
}
